import Container from '@/components/ui/Container/Container';
import { cn } from '@/components/ui/utils';
import ContactInquiryPanel from '@/app/[locale]/contact/components/ContactInquiryPanel';
import FaqEnquiryGrid from '@/components/sections/Products/FaqEnquiryGrid';
import { HOME_SECTION_PADDING_CLASS } from '@/lib/home-section-spacing';
import type { LeadForm } from '@/types/lead-forms';

interface FAQ {
  id: number;
  faqTitle: string;
  faqContent: string;
}

interface FormFieldData {
  id?: string | number;
  label?: string;
  placeholder?: string;
  required?: boolean | null;
  type?: string;
  fieldType?: 'text' | 'tel' | 'email';
  checkboxLabel?: string;
}

interface FormData {
  id: number;
  formTitle?: string;
  formDescription?: string;
  inputType?: FormFieldData[];
  selectType?: FormFieldData[];
  textArea?: FormFieldData[];
  // The CMS may emit either a plain config object or an array of repeater
  // entries. The runtime branches on Array.isArray below to normalize.
  stateDistrictDropdown?:
    | {
        enabled?: boolean;
        stateLabel?: string;
        districtLabel?: string;
      }
    | Array<{
        id?: number;
        stateFieldLabel?: string;
        stateFieldId?: string;
        districtFieldLabel?: string;
        districtFieldId?: string;
        states?: unknown[];
      }>;
  radioButton?: FormFieldData[] | unknown[];
  checkbox?: FormFieldData[];
  submitButton?: { id?: number; buttonText?: string; disabled?: boolean | null } | null;
}

interface FaqWithFormComponent {
  __component: 'modules.faqwith-form';
  id: number;
  heading: string;
  subheading?: string | null;
  faqs: FAQ[];
  form?: FormData | null;
  lead_forms?: LeadForm[];
}

interface FaqEnquirySectionProps {
  faqComponent?: FaqWithFormComponent;
  heading?: string;
  /**
   * CMS-driven "Connect with Wonder Cement" categories. Pass via
   * `await getLeadFormsWithFallback(locale)` from any server-component
   * parent. When omitted (or empty), the embedded `ContactInquiryPanel`
   * falls back to its hardcoded 8-card list, so pages that haven't been
   * wired yet still render correctly.
   */
  leadForms?: LeadForm[];
  hasFaqs?: boolean;
  /** Additional section wrapper classes (merged with defaults). */
  sectionClassName?: string;
}

export default function FaqEnquirySection({
  faqComponent,
  heading = 'Frequently Asked Questions',
  leadForms,
  hasFaqs: shouldRenderFaqs = true,
  sectionClassName,
}: FaqEnquirySectionProps) {
  const displayHeading = faqComponent?.heading || heading;
  // Source FAQs strictly from CMS. The previous hardcoded English fallback
  // was misleading on non-English locales (e.g. /hi showed Hindi heading +
  // English questions because the Hindi entry hadn't been authored yet).
  // When CMS returns no FAQs, the layout collapses to a single centered
  // enquiry column instead of inventing content.
  const faqs = faqComponent?.faqs ?? [];
  const hasFaqs = shouldRenderFaqs && faqs.length > 0;
  return (
    <section
      id="faqs"
      className={cn('relative bg-black text-white', HOME_SECTION_PADDING_CLASS, sectionClassName)}
    >
      <Container>
        <div className="mx-auto mb-8 max-w-4xl text-center sm:mb-10">
          <h3 className="text-[1.75rem] font-semibold tracking-tight sm:text-4xl md:text-6xl">
            {displayHeading}
          </h3>
          {faqComponent?.subheading && (
            <p className="mt-2 text-sm leading-relaxed text-white/80 sm:mt-3 sm:text-base">
              {faqComponent.subheading}
            </p>
          )}
        </div>

        {hasFaqs ? (
          <FaqEnquiryGrid faqs={faqs} leadForms={leadForms} />
        ) : (
          <div className="mx-auto max-w-3xl">
            <ContactInquiryPanel leadForms={leadForms} />
          </div>
        )}
      </Container>
    </section>
  );
}
